import { type EnumContainerActionRequest, type LinkableField } from '@Model';

/** A line a follow-up may be planned after, which stands for a customer at a site. */
export interface SelectableRow {
    id: string;
    customer: LinkableField;
    site: LinkableField;
    allowed_follow_ups: readonly EnumContainerActionRequest[];
}

/** Lines of one customer at one site that are shown as one row. */
export interface SelectableGroup<TRow extends SelectableRow> extends Pick<SelectableRow, 'customer' | 'site'> {
    id: string;
    lines: readonly TRow[];
}

/** Whether the row may be selected, which it is where a follow-up may come after it. */
// @ai-generated 2026-10-09T12:00:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function canBeSelected(row: Pick<SelectableRow, 'allowed_follow_ups'>): boolean {
    return row.allowed_follow_ups.length > 0;
}

/** Whether the group has a line that may be selected, and every such line is selected. */
// @ai-generated 2026-10-05T10:28:57+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function isGroupSelected<TRow extends SelectableRow>(
    group: SelectableGroup<TRow>,
    selected: ReadonlySet<string>,
): boolean {
    const selectable = group.lines.filter(canBeSelected);

    return selectable.length > 0 && selectable.every((line) => selected.has(line.id));
}

/**
 * The selection once exactly the groups with the given ids are ticked: every selectable line of a newly ticked
 * group is selected, and every line of a group that was fully selected and is no longer ticked is deselected.
 */
// @ai-generated 2026-10-05T10:28:57+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function withTickedGroups<TRow extends SelectableRow>(
    selected: ReadonlySet<string>,
    groups: readonly SelectableGroup<TRow>[],
    tickedIds: readonly string[],
): ReadonlySet<string> {
    const ticked = new Set(tickedIds);
    const next = new Set(selected);

    for (const group of groups) {
        if (ticked.has(group.id)) {
            group.lines.filter(canBeSelected).forEach((line) => next.add(line.id));
        } else if (isGroupSelected(group, selected)) {
            group.lines.forEach((line) => next.delete(line.id));
        }
    }

    return next;
}

/** The selection once exactly the given lines of the group are ticked, with the rest of the selection as it was. */
// @ai-generated 2026-10-05T10:28:57+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function withTickedLines<TRow extends SelectableRow>(
    selected: ReadonlySet<string>,
    group: SelectableGroup<TRow>,
    tickedIds: readonly string[],
): ReadonlySet<string> {
    const next = new Set(selected);
    const ticked = new Set(tickedIds);

    for (const line of group.lines) {
        if (ticked.has(line.id)) {
            next.add(line.id);
        } else {
            next.delete(line.id);
        }
    }

    return next;
}

/** Whether the row stands for the customer and at the site of the first selected row, or nothing is selected yet. */
// @ai-generated 2026-10-05T10:28:57+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function fitsSelection<TRow extends SelectableRow>(
    row: Pick<SelectableRow, 'customer' | 'site'>,
    selected: ReadonlySet<string>,
    rowsById: ReadonlyMap<string, TRow>,
): boolean {
    const [first] = selected;
    const anchor = first === undefined ? undefined : rowsById.get(first);

    return (
        anchor === undefined ||
        (row.customer.foreign_key === anchor.customer.foreign_key && row.site.foreign_key === anchor.site.foreign_key)
    );
}

/** The selection without the rows that are no longer listed. */
// @ai-generated 2026-10-05T10:28:57+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function stillListed<TRow extends SelectableRow>(
    selected: ReadonlySet<string>,
    rowsById: ReadonlyMap<string, TRow>,
) {
    const kept = [...selected].filter((id) => rowsById.has(id));

    return kept.length === selected.size ? selected : new Set(kept);
}

/** The follow-ups that every selected row allows, which is none while nothing is selected. */
// @ai-generated 2026-10-08T10:05:14+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function followUpsAllowedFor<TRow extends SelectableRow>(
    selected: ReadonlySet<string>,
    rowsById: ReadonlyMap<string, TRow>,
): EnumContainerActionRequest[] {
    const rows = [...selected].flatMap((id) => rowsById.get(id) ?? []);
    if (rows.length === 0) {
        return [];
    }

    return rows[0].allowed_follow_ups.filter((kind) => rows.every((row) => row.allowed_follow_ups.includes(kind)));
}
